import { createSelector } from "reselect";
import { makeSelectQuestionnaireCategory } from "../questionnaireCategories/selectors";
import { makeSelectStringSource } from "../stringResources/selectors";
import { makeSelectTranslation } from "../translation/selectors";
import { createMakeSelectCurrentPage } from "lib/providers/pagination-provider/selectors";
import { makeSelectLanguages } from "../languages/selectors";
import { makeSelectEntityStatuses } from "../entity_status";

const selectEntity = state => state.entities.questionnaire;
const selectEntityData = state =>
  state.entities.questionnaire ? state.entities.questionnaire.data : {};

export const makeSelectQuestionnaires = paginationKey =>
  createSelector(
    [
      selectEntityData,
      createMakeSelectCurrentPage(paginationKey)(),
      makeSelectQuestionnaireCategory(),
      makeSelectEntityStatuses()
    ],
    (substate, page, categories, entityStatuses) => {
      if (!substate) {
        return [];
      }

      if (!paginationKey) {
        const result = {};
        for (let key in substate) {
          const questionnaire = { ...substate[key] };

          if (questionnaire.entityStatus)
            questionnaire.entityStatus =
              entityStatuses[questionnaire.entity_status.id];

          const newCategories = [];
          if (questionnaire.categories)
            if (
              questionnaire.categories &&
              questionnaire.categories.length > 0
            ) {
              for (let category of questionnaire.categories) {
                newCategories.push(categories[category.id]);
              }

              questionnaire.categories = newCategories;
            }

          result[questionnaire.id] = questionnaire;
        }

        return result;
      }

      if (page.length > 0)
        return page
          .map(id => {
            if (!substate[id]) {
              return null;
            }

            const questionnaire = { ...substate[id] };
            questionnaire.entityStatus =
              entityStatuses[questionnaire.entity_status.id];

            const newCategories = [];

            if (
              questionnaire.categories &&
              questionnaire.categories.length > 0
            ) {
              for (let category of questionnaire.categories) {
                newCategories.push(categories[category.id]);
              }

              questionnaire.categories = newCategories;
            }

            return questionnaire;
          })
          .filter(row => row);
    }
  );

export const makeSelectQuestionnaire = () =>
  createSelector(
    [selectEntityData, makeSelectQuestionnaireCategory()],
    (substate, categories) => {
      const result = {};
      for (let key in substate) {
        const questionnaire = { ...substate[key] };

        const newCategories = [];

        for (let category of questionnaire.categories) {
          newCategories.push(categories[category.id]);
        }

        questionnaire.categories = newCategories;
        result[key] = questionnaire;
      }

      return result;
    }
  );

export const makeSelectOneQuestionnaire = id =>
  createSelector(
    [
      selectEntityData,
      makeSelectQuestionnaireCategory(),
      makeSelectStringSource(),
      makeSelectTranslation(),
      makeSelectLanguages()
    ],
    (substate, categories, resources, translations, languages) => {
      if (!substate) {
        return {};
      }

      const questionnaire = substate[id];
      if (!questionnaire) {
        return {};
      }

      const newCategories = [];
      for (let category of questionnaire.categories) {
        newCategories.push(categories[category.id]);
      }

      for (let resource of resources) {
        if (resource.id === questionnaire.string_resources.id) {
          questionnaire.string_resources = resource;
        }
        if (questionnaire.descriptions) {
          if (resource.id === questionnaire.descriptions.id) {
            questionnaire.descriptions = resource;
          }
        }
      }

      const newTranslations = [];
      for (let translation of questionnaire.string_resources.translations) {
        newTranslations.push(translations[translation.id]);
      }
      questionnaire.string_resources.translations = newTranslations;

      for (let languageQuestionnaire of questionnaire.string_resources
        .translations) {
        for (let language of languages) {
          if (languageQuestionnaire.language.id === language.id)
            languageQuestionnaire.language = language;
        }
      }

      let descriptionValues = {};

      if (questionnaire.descriptions) {
        const descriptionsTranslations = [];
        for (let translation of questionnaire.descriptions.translations) {
          descriptionsTranslations.push(translations[translation.id]);
        }
        questionnaire.descriptions.translations = descriptionsTranslations;

        for (let translation of questionnaire.descriptions.translations) {
          descriptionValues["description_" + translation.language.id] =
            translation.translation;
        }
      }

      questionnaire.categories = newCategories;

      const nameValues = {};

      for (let translation of questionnaire.string_resources.translations) {
        nameValues[translation.language.id] = translation.translation;
      }

      questionnaire["initialValues"] = {
        name: nameValues,
        descriptions:
          Object.keys(descriptionValues).length > 0 ? descriptionValues : [],
        imageId: questionnaire.image_id
      };

      return questionnaire;
    }
  );

export const makeSelectIsQuestionnaireLoading = () =>
  createSelector(selectEntity, substate => {
    if (substate) return substate.isLoading;
  });
